import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { LoadingState } from '../../types';
import retreatStyleService, { RetreatStyle } from '../../services/retreatStyleService';

interface RetreatStyleState extends LoadingState {
  retreatStyles: RetreatStyle[];
  selectedRetreatStyle: RetreatStyle | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasMore: boolean;
  };
  searchQuery: string;
}

const initialState: RetreatStyleState = {
  retreatStyles: [],
  selectedRetreatStyle: null,
  pagination: {
    page: 1,
    limit: 5,
    total: 0,
    pages: 0,
    hasMore: false,
  },
  searchQuery: '',
  isLoading: false,
  error: null,
};

// Async Thunks
export const fetchRetreatStyles = createAsyncThunk(
  'retreatStyles/fetchRetreatStyles',
  async (params: { page?: number; limit?: number; search?: string; append?: boolean } = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 5, search = '', append = false } = params;
      const response = await retreatStyleService.getActiveRetreatStyles({ page, limit });
      return { ...response, append };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch retreat styles');
    }
  }
);

export const createRetreatStyle = createAsyncThunk(
  'retreatStyles/createRetreatStyle',
  async (data: { name: string; status: 'active' | 'inactive' }, { rejectWithValue }) => {
    try {
      const response = await retreatStyleService.createRetreatStyle(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create retreat style');
    }
  }
);

const retreatStyleSlice = createSlice({
  name: 'retreatStyles',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedRetreatStyle: (state, action: PayloadAction<RetreatStyle | null>) => {
      state.selectedRetreatStyle = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    resetPagination: (state) => {
      state.pagination = initialState.pagination;
      state.retreatStyles = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Retreat Styles
      .addCase(fetchRetreatStyles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRetreatStyles.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, append } = action.payload;
        
        if (append) {
          state.retreatStyles.push(...data.data as any);
        } else {
          state.retreatStyles = data.data as any;
        }
        
        state.pagination = {
          page: data.pagination.page,
          limit: data.pagination.limit,
          total: data.pagination.total,
          pages: data.pagination.pages || data.pagination.totalPages,
          hasMore: data.pagination.page < (data.pagination.pages || data.pagination.totalPages),
        };
      })
      .addCase(fetchRetreatStyles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Retreat Style
      .addCase(createRetreatStyle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createRetreatStyle.fulfilled, (state, action) => {
        state.isLoading = false;
        const newStyle = action.payload;
        
        // Create a properly typed object for WritableDraft
        const styleToAdd: RetreatStyle = {
          _id: newStyle._id,
          name: newStyle.name,
          slug: newStyle.slug,
          status: newStyle.status,
          isDeleted: newStyle.isDeleted,
          createdBy: newStyle.createdBy,
          createdAt: newStyle.createdAt,
          updatedAt: newStyle.updatedAt
        };
        
        state.retreatStyles.unshift(styleToAdd as any);
        state.pagination.total += 1;
      })
      .addCase(createRetreatStyle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setSelectedRetreatStyle,
  setSearchQuery,
  resetPagination,
} = retreatStyleSlice.actions;

export default retreatStyleSlice.reducer;
